import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Drawer, Form, Input, Radio, Space, Button, Empty, message } from "antd";
import {
  privateGetApi,
  privatePutApi,
} from "../../utils/apiHelper";

// Lightweight TinyMCE-based editor for templates, mirroring the Quick
// Campaign drawer's editor config. Kept separate from the GrapesJS "Editor"
// page so a template authored with TinyMCE (simple HTML) can be edited the
// same way it was created, instead of being force-parsed through GrapesJS's
// block-based canvas.
export default function TemplateQuickEditor({ templateId, open, onClose, onSaved }) {
  const editorRef = useRef(null);
  const [form] = Form.useForm();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !templateId) return;
    setLoading(true);
    setTemplate(null);
    privateGetApi(`/templates/${templateId}`, {})
      .then((res) => {
        const tpl = res?.data?.result;
        setTemplate(tpl || null);
        form.setFieldsValue({
          name: tpl?.name || "",
          subject: tpl?.subject || "",
          visibility: tpl?.visibility === "public" ? "public" : "private",
        });
      })
      .catch(() => message.error("Failed to load template."))
      .finally(() => setLoading(false));
  }, [open, templateId, form]);

  const handleSave = (values) => {
    const content = editorRef.current?.getContent();
    if (!content || !content.replace(/<[^>]+>/g, "").trim()) {
      message.error("Template content is empty.");
      return;
    }
    setSaving(true);
    privatePutApi(`/templates/${templateId}`, {
      name: values.name.trim(),
      subject: (values.subject || "").trim(),
      content,
      visibility: values.visibility,
    })
      .then(() => {
        message.success("Template updated.");
        onSaved?.();
        onClose();
      })
      .catch(() => message.error("Failed to update template."))
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      title="Edit Template (Quick Editor)"
      width="min(720px, 90vw)"
      placement="right"
      maskClosable={false}
      destroyOnClose
      onClose={onClose}
      open={open}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={form.submit} loading={saving}>
            Save
          </Button>
        </Space>
      }
    >
      {!template && !loading ? (
        <Empty description="Template not found." />
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: "Enter template name" }]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>
          <Form.Item name="subject" label="Template Subject (optional)">
            <Input placeholder="Optional subject line" />
          </Form.Item>
          <Form.Item name="visibility" label="Visibility">
            <Radio.Group>
              <Radio value="private">Private (only your org)</Radio>
              <Radio value="public">Public (shared with all orgs)</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="Content">
            {template ? (
              <Editor
                tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                }}
                initialValue={template.content || ""}
                init={{
                  skin: "oxide",
                  content_css: "default",
                  branding: false,
                  height: "calc(100vh - 320px)",
                  menubar: false,
                  selector: "textarea",
                  convert_urls: true,
                  relative_urls: false,
                  remove_script_host: false,
                  image_title: true,
                  automatic_uploads: true,
                  file_picker_types: "image",
                  file_picker_callback: (cb) => {
                    const input = document.createElement("input");
                    input.setAttribute("type", "file");
                    input.setAttribute("accept", "image/*");
                    input.addEventListener("change", (e) => {
                      const file = e.target.files[0];
                      const reader = new FileReader();
                      reader.addEventListener("load", () => {
                        const id = "blobid" + new Date().getTime();
                        /* global tinymce */
                        const blobCache = tinymce.activeEditor.editorUpload.blobCache;
                        const base64 = reader.result.split(",")[1];
                        const blobInfo = blobCache.create(id, file, base64);
                        blobCache.add(blobInfo);
                        cb(blobInfo.blobUri(), { title: file.name });
                      });
                      reader.readAsDataURL(file);
                    });
                    input.click();
                  },
                  plugins: [
                    "advlist",
                    "autolink",
                    "lists",
                    "link",
                    "image",
                    "charmap",
                    "preview",
                    "insertdatetime",
                    "anchor",
                    "searchreplace",
                    "visualblocks",
                    "code",
                    "media",
                    "table",
                    "wordcount",
                    "quickbars",
                    "emoticons",
                    "fullscreen",
                    "importcss",
                    "directionality",
                    "visualchars",
                    "codesample",
                    "pagebreak",
                    "nonbreaking",
                    "help",
                    "accordion",
                  ],
                  toolbar: [
                    "undo redo | fontfamily fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | strikethrough subscript superscript | blockquote removeformat",
                    "link emoticons charmap image media table codesample | code preview fullscreen",
                  ],
                }}
              />
            ) : null}
          </Form.Item>
        </Form>
      )}
    </Drawer>
  );
}
